// 多语言与站点语言切换
import { getPortalLocales } from '@/api/portal'
import { HOME_PATH } from '@/components/layout/utils/navMenu'
import { normalizeContentPath } from '@/components/portal/common/utils/menuPath'
import { isLocaleHomePath } from '@/components/portal/common/utils/localePath'
import { resolveLocaleForVisit } from '@/components/portal/common/utils/localeNavigation'
import { resolvePortalContext } from '@/components/portal/common/utils/portalRouteResolver'
import { loadPortalPageData } from '@/components/portal/common/utils/portalPageLoader'
import { syncRichContentUiLabels } from '@/components/shared/utils/richContentUiLabels'

const LEGACY_LOCALE_KEY = 'locale'
const LEGACY_USER_LOCALE_KEY = 'locale_user'
const LEGACY_MANUAL_KEY = 'locale_manual'
const PORTAL_LOCALE_KEY = 'portal_locale'
const PORTAL_LOCALE_MANUAL_KEY = 'portal_locale_manual'
const PORTAL_SESSION_LOCALE_KEY = 'portal_session_locale'
let inflightFetchLocales = null

function readSessionLocale() {
  try {
    return (sessionStorage.getItem(PORTAL_SESSION_LOCALE_KEY) || '').trim()
  } catch (_) {
    return ''
  }
}

function saveSessionLocale(code) {
  if (!code) return
  try {
    sessionStorage.setItem(PORTAL_SESSION_LOCALE_KEY, code)
  } catch (_) { /* ignore */ }
}

function getWindowPathAndQuery() {
  if (typeof window === 'undefined') {
    return { path: HOME_PATH, query: {} }
  }
  const publicPath = String(process.env.BASE_URL || '/').replace(/\/+$/, '')
  let path = window.location.pathname || HOME_PATH
  if (publicPath && publicPath !== '/' && path.startsWith(publicPath)) {
    path = path.slice(publicPath.length) || HOME_PATH
  }
  const query = {}
  try {
    const params = new URLSearchParams(window.location.search)
    params.forEach((value, key) => {
      query[key] = value
    })
  } catch (_) { /* ignore */ }
  return { path, query }
}

function resolveStartupLocale({ enabled, currentLocale = '' }) {
  const { path, query } = getWindowPathAndQuery()
  return resolveLocaleForVisit({
    path,
    query,
    enabled,
    sessionLocale: readSessionLocale(),
    currentLocale,
    isInitial: !currentLocale
  })
}

function clearPersistedLocaleStorage() {
  localStorage.removeItem(LEGACY_LOCALE_KEY)
  localStorage.removeItem(LEGACY_USER_LOCALE_KEY)
  localStorage.removeItem(LEGACY_MANUAL_KEY)
  localStorage.removeItem(PORTAL_LOCALE_KEY)
  localStorage.removeItem(PORTAL_LOCALE_MANUAL_KEY)
}

const state = {
  locales: [],
  current: '',
  loaded: false,
  loading: false,
  error: ''
}

const getters = {
  currentLocale(state) {
    return state.locales.find((item) => item.code === state.current) || null
  },
  localeDir(state, getters) {
    return getters.currentLocale?.rtl ? 'rtl' : 'ltr'
  }
}

const mutations = {
  SET_LOCALES(state, locales) {
    state.locales = locales
    state.loaded = true
    state.error = ''
  },
  SET_CURRENT(state, code) {
    state.current = code
    syncRichContentUiLabels(code)
  },
  SET_LOADING(state, loading) {
    state.loading = loading
  },
  SET_ERROR(state, error) {
    state.error = error || ''
  },
  FINISH_INITIAL_LOAD(state) {
    state.loaded = true
  }
}

const SPECIAL_APP_PREFIXES = ['/products/detail', '/products/catalog', '/search']

function getCurrentPortalPath() {
  const { path } = getWindowPathAndQuery()
  return normalizeContentPath(path) || HOME_PATH
}

function isSpecialAppPath(path) {
  return SPECIAL_APP_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`))
}

async function reloadLocaleContent({ commit, dispatch }, previousLocale) {
  if (!previousLocale) return

  await dispatch('news/resetForLocaleChange', null, { root: true })
  await dispatch('products/resetForLocaleChange', null, { root: true })
  commit('join/RESET', null, { root: true })
  commit('hero/RESET', null, { root: true })
  commit('homeSections/RESET', null, { root: true })
  commit('productCategories/RESET', null, { root: true })
  commit('solutions/RESET', null, { root: true })
  commit('about/RESET', null, { root: true })
  await dispatch('legalPage/resetForLocaleChange', null, { root: true })
}

function createRootDispatch(dispatch, rootState) {
  return {
    state: rootState,
    dispatch: (action, payload) => dispatch(action, payload, { root: true })
  }
}

async function fetchCurrentPageContent({ dispatch, rootState }, { force = false } = {}) {
  const path = getCurrentPortalPath()
  if (path === HOME_PATH || isLocaleHomePath(path)) {
    await Promise.all([
      dispatch('hero/fetchHero', { force }, { root: true }),
      dispatch('homeSections/fetchHomeSections', { force }, { root: true })
    ])
    return
  }

  if (isSpecialAppPath(path)) return

  const context = resolvePortalContext(
    path,
    rootState.nav?.rawMenus || [],
    rootState.footer?.data?.links || [],
    rootState.locale?.current
  )
  await loadPortalPageData(createRootDispatch(dispatch, rootState), context, { path }, { force })
}

async function fetchLocaleContent({ dispatch, rootState }, { force = false } = {}) {
  await Promise.all([
    dispatch('nav/fetchMenus', { force }, { root: true }),
    dispatch('footer/fetchFooter', { force }, { root: true }),
    dispatch('webmaster/fetchWebmaster', { force }, { root: true }),
    // 产品指南 mega menu 依赖分类树，需与 menus 同步按语言拉取
    dispatch('productCategories/fetchCategories', { force }, { root: true })
  ])
  await fetchCurrentPageContent({ dispatch, rootState }, { force })
}

const actions = {
  async fetchLocales({ commit, state, dispatch, rootState }, options) {
    const { force = false } = options ?? {}
    if (!force && state.loaded && state.current && !state.loading) {
      return
    }

    if (inflightFetchLocales) {
      return inflightFetchLocales
    }

    const task = (async () => {
      commit('SET_LOADING', true)
      commit('SET_ERROR', '')
      try {
        const locales = await getPortalLocales()
        const enabled = (locales || []).filter((item) => item.enabled !== false)
        commit('SET_LOCALES', enabled)
        clearPersistedLocaleStorage()

        const previousLocale = state.current
        const resolved = resolveStartupLocale({ enabled, currentLocale: previousLocale })
        const nextLocale = resolved.locale

        if (nextLocale) {
          const localeChanged = nextLocale !== previousLocale
          if (localeChanged && previousLocale) {
            await reloadLocaleContent({ commit, dispatch }, previousLocale)
          }
          commit('SET_CURRENT', nextLocale)
          saveSessionLocale(nextLocale)
        }

        await fetchLocaleContent({ dispatch, rootState }, { force })
      } catch (error) {
        commit('SET_ERROR', error?.message || '网络异常')
        if (!state.loaded) {
          commit('FINISH_INITIAL_LOAD')
        }
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    })()

    inflightFetchLocales = task
    try {
      await task
    } finally {
      if (inflightFetchLocales === task) {
        inflightFetchLocales = null
      }
    }
  },
  async setLocale({ commit, dispatch, state, rootState }, code) {
    if (code === state.current) return

    const previousLocale = state.current
    await reloadLocaleContent({ commit, dispatch }, previousLocale)
    commit('SET_CURRENT', code)
    saveSessionLocale(code)
    await fetchLocaleContent({ dispatch, rootState })
  },
  persistSessionLocale({ state }) {
    saveSessionLocale(state.current)
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
